import { useMemo } from "react";
import { UAParser } from "ua-parser-js";

interface DeviceType {
  isAndroid: boolean;
  isIOS: boolean;
  isMobile: boolean;
}

/**
 * Hook to detect the user's device type and platform
 * @returns Object containing device detection flags
 */
export const useDeviceType = (): DeviceType => {
  const deviceInfo = useMemo(() => {
    if (typeof window === "undefined") {
      return { isAndroid: false, isIOS: false, isMobile: false };
    }

    const parser = new UAParser(window.navigator.userAgent);
    const result = parser.getResult();

    const osName = result.os.name?.toLowerCase() || "";
    const deviceType = result.device.type?.toLowerCase() || "";
    const userAgent = window.navigator.userAgent.toLowerCase();

    // Detect Android devices - must be mobile/tablet device type to avoid desktop Chrome
    const isAndroid = osName.includes("android") && (deviceType === "mobile" || deviceType === "tablet");

    // Detect iOS devices (iPhone, iPad, iPod)
    // Only detect mobile iOS devices, not desktop Safari
    // Check for mobile Safari user agent patterns
    const isIOSUserAgent = /iphone|ipod/.test(userAgent) || (/ipad/.test(userAgent) && deviceType === "tablet");

    const isIOS =
      (osName.includes("ios") || osName.includes("iphone") || osName.includes("ipad") || osName.includes("ipod")) &&
      (deviceType === "mobile" || deviceType === "tablet" || isIOSUserAgent);

    // Check if it's a mobile device
    const isMobile = deviceType === "mobile" || deviceType === "tablet" || isAndroid || isIOS;

    return {
      isAndroid,
      isIOS,
      isMobile,
    };
  }, []);

  return deviceInfo;
};
